import { observer } from "mobx-react";
import React, { useState, useEffect, useContext } from "react";
import { Spin } from "antd";
import { useLocation, useHistory, Route } from "react-router-dom";
import { parse } from "query-string";
import { CognitoIdToken, CognitoUser } from "amazon-cognito-identity-js";
import { SessionContext, SessionServiceInstance } from "../../services/session";
import { PARAMS, Routes } from "../../utils/routes";
import { isPublicSquadRecapPath } from "../../utils/publicOperatorPaths";
import { COLORS } from "../../styled/basic";
import { UsersServiceInstance } from "../../services/users";
import { WebAppPort, WebAppProtocol, WebAppSubdomain, TruncatedHostname } from "../../utils/domainAnalysis";
import {
  applyOperatorRoleFlagsFromGroups,
  defaultLandingRouteFromGroups,
} from "../../utils/operatorRoles";

// Hosted-UI config is supplied at runtime via window.appConfig (loaded from /config/config.js,
// per-environment and ultimately owned by Terraform). Fall back to the historical hardcoded
// values so existing deployments keep working until their config.js carries the fields.
type OperatorHostedUiConfig = {
  cognitoHostedUiDomain?: string;
  cognitoRegion?: string;
  cognitoHostedUiClientId?: string;
};
const hostedUiConfig = (window as { appConfig?: OperatorHostedUiConfig }).appConfig;
const COGNITO_DOMAIN = hostedUiConfig?.cognitoHostedUiDomain ?? "dev-momento-operator";
const COGNITO_REGION = hostedUiConfig?.cognitoRegion ?? "us-west-2";
const COGNITO_HOSTED_UI_CLIENT_ID = hostedUiConfig?.cognitoHostedUiClientId ?? "2c2l3f2728o74j32v1ba9b7p"; // dev-operatorApp-client

let cognitoHostedUIRedirectUri = `${window.location.origin}/`;
let cognitoHostedUILoginURL = `https://${COGNITO_DOMAIN}.auth.${COGNITO_REGION}.amazoncognito.com/login?client_id=${COGNITO_HOSTED_UI_CLIENT_ID}&response_type=code&scope=email+openid+phone+profile&redirect_uri=${encodeURIComponent(cognitoHostedUIRedirectUri)}`;
let cognitoHostedUILogoutURL = `https://${COGNITO_DOMAIN}.auth.${COGNITO_REGION}.amazoncognito.com/logout?client_id=${COGNITO_HOSTED_UI_CLIENT_ID}&response_type=code&scope=email+openid+phone+profile&redirect_uri=${encodeURIComponent(cognitoHostedUIRedirectUri)}`;

const DEFAULT_PAGE = Routes.GAMES;

function resolvePostLoginRoute(groups: string[]): string {
  if (SessionServiceInstance.profileComplete === false) {
    return Routes.ACCOUNT;
  }
  return defaultLandingRouteFromGroups(groups);
}

interface LoginComponentProps {
  cognitoUser? : CognitoUser;
  userAttributes?: any;
}

let subdomainAnalysisComplete = false;
const subdomainAnalysis = (): void => {
  if (subdomainAnalysisComplete === false) {
    // determine if the site was accessed through a subdomain, in which case we need to
    // reset the cognitoHostedUIRedirectUri to the base URL of the subdomain
    const INITIAL_PROTOCOL = WebAppProtocol();
    const TRUNCATED_HOSTNAME = TruncatedHostname();
    const APP_SUBDOMAIN = WebAppSubdomain();
    const INITIAL_PORT = WebAppPort();

    console.log(`login.tsx / subdomainAnalysis() - subdomain=${APP_SUBDOMAIN}`);
    if (APP_SUBDOMAIN !== "") {
      cognitoHostedUIRedirectUri = `${INITIAL_PROTOCOL}//${TRUNCATED_HOSTNAME}${(INITIAL_PORT === "") ? "" : `:${INITIAL_PORT}`}/`;
      cognitoHostedUILoginURL = `https://${COGNITO_DOMAIN}.auth.${COGNITO_REGION}.amazoncognito.com/login?client_id=${COGNITO_HOSTED_UI_CLIENT_ID}&response_type=code&scope=email+openid+phone+profile&redirect_uri=${encodeURIComponent(cognitoHostedUIRedirectUri)}&state=${encodeURIComponent(window.location.href)}`;
      cognitoHostedUILogoutURL = `https://${COGNITO_DOMAIN}.auth.${COGNITO_REGION}.amazoncognito.com/logout?client_id=${COGNITO_HOSTED_UI_CLIENT_ID}&response_type=code&scope=email+openid+phone+profile&redirect_uri=${encodeURIComponent(cognitoHostedUIRedirectUri)}&state=${encodeURIComponent(window.location.href)}`;
    }

    subdomainAnalysisComplete = true;
  }
};

export const CognitoHostedUILoginURL = (): string => {
  subdomainAnalysis();
  return cognitoHostedUILoginURL;
};

export const CognitoHostedUILogoutURL = (): string => {
  subdomainAnalysis();
  return cognitoHostedUILogoutURL;
};

export const CognitoHostedUIRedirectUri = (): string => {
  subdomainAnalysis();
  return cognitoHostedUIRedirectUri;
};

const LoginPage = observer((): JSX.Element => {
  const [isAuthorized, setAuthorized] = useState(true);
  const [authorizedSites, setAuthorizedSites] = useState([] as any[]);

  const { search, pathname } = useLocation();
  const parsedQueryParams = parse(search);

  const sessionContext = useContext(SessionContext);

  const exchangeCodeForTokens = async (): Promise<any> => {
    const code = parsedQueryParams[PARAMS.CODE] as string;

    const result = await UsersServiceInstance.getCognitoTokens(
      code, CognitoHostedUIRedirectUri(),
    );

    return result;
  };

  const getRedirectURL = (subdomain): string => {
    const INITIAL_PROTOCOL = WebAppProtocol();
    const TRUNCATED_HOSTNAME = TruncatedHostname();
    const INITIAL_PORT = WebAppPort();

    const code = parsedQueryParams[PARAMS.CODE] as string;

    const redirectUrl = `${INITIAL_PROTOCOL}//${(subdomain === "") ? "" : `${subdomain}.`}${TRUNCATED_HOSTNAME}${(INITIAL_PORT === "") ? "" : `:${INITIAL_PORT}`}/`; // ?code=${code}
    console.log(`LoginPage.getRedirectURL(subdomain=${subdomain}) - redirectUrl=${redirectUrl}`);
    return redirectUrl;
  };

  const setSession = async (): Promise<void> => {
    console.log("LoginPage.setSession() - start", search, parsedQueryParams);

    const squadRecapToken = parsedQueryParams.squadRecapToken as string;
    if (squadRecapToken) {
      window.location.replace(`/squad-recap/${encodeURIComponent(squadRecapToken)}`);
      return;
    }

    // aws cognitio redirected back with provided code
    const code = parsedQueryParams[PARAMS.CODE] as string;
    const state = parsedQueryParams[PARAMS.STATE] as string;
    if (code) {
      console.log(`LoginPage.setSession() - Code=${code}, state=${state}`);

      // if state is set, it is because the site was accessed throught a subdomain;
      //  immediately redirect
      if (state) {
        if (isPublicSquadRecapPath(state)) {
          window.location.href = state;
          return;
        }
        const urlParamPrefix = state.includes("?") ? "&" : "?";
        // append the code as a param, so that the subdomain version of this page
        // can record the code in a session associated with the subdomain
        const redirectUrl = `${state}${urlParamPrefix}${PARAMS.CODE}=${code}`;
        window.location.href = redirectUrl;
        return;
      }

      try {
        const userTokens = await exchangeCodeForTokens();
        console.log("LoginPage.setSession() - exchange for tokens", { ...userTokens });

        const cognitoIdToken = new CognitoIdToken({ IdToken: userTokens.id_token });
        const idTokenPayload = cognitoIdToken.decodePayload();
        console.log("LoginPage.setSession() - accessTokenPayload=", { ...idTokenPayload });

        // the user is authenticated into the Momento system;
        //   now we need to ensure that the user is authorized for this stadium
        const webAppSubdomain = WebAppSubdomain();

        console.log(`LoginPage.setSession() - subdomain=${webAppSubdomain}`);
        const cognitoGroups = idTokenPayload["cognito:groups"] || [];
        const { isSuperAdmin, isIndexingOperator } = applyOperatorRoleFlagsFromGroups(
          cognitoGroups,
        );
        const hasTeamGroup = cognitoGroups.some(
          (item: string) => /^teamAdministrators\.(\w+)$/.test(item),
        );

        const userIsAuthorized = isSuperAdmin
          || isIndexingOperator
          || hasTeamGroup
          || !!idTokenPayload.email;

        if (!userIsAuthorized) {
          setAuthorized(false);
          return;
        }

        // store access token, for all pertinent subsequent API calls to establish identity
        sessionContext.setSession({
          AccessToken: userTokens.access_token,
          IdToken: userTokens.id_token,
          RefreshToken: userTokens.refresh_token,
          EmailAddress: idTokenPayload.email,
        });

        const sessionData = await sessionContext.getSession();
        console.log("LoginPage.setSession(): session persisted: ", sessionData);

        // redirect to the application after token receiving
        window.location.href = resolvePostLoginRoute(cognitoGroups);
        return;
      } catch (err) {
        console.log("LoginPage.setSession() - error=", err);
      }
    }

    // we have not code or we have error during token receiving
    try {
      const sessionData = await sessionContext.getSession();
      console.log("LoginPage.setSession() - current session", { ...sessionData });

      // redirect to the application if we have saved token
      // TODO: check for expiration?
      if (sessionData && sessionData.AccessToken) {
        const idToken = sessionData.IdToken;
        let groups: string[] = [];
        if (idToken) {
          try {
            const cognitoIdToken = new CognitoIdToken({ IdToken: idToken });
            groups = cognitoIdToken.decodePayload()["cognito:groups"] || [];
          } catch {
            groups = [];
          }
        }
        window.location.href = resolvePostLoginRoute(groups);
        return;
      }
    } catch (err) {
      // TODO:  do this in a "React-friendly" way
      console.log("LoginPage.setSession() - error = ", err);
    }

    // no code and no saved session - open cognitio page
    const redirectUri = CognitoHostedUIRedirectUri();
    const loginUrl = CognitoHostedUILoginURL();
    console.warn("[Cognito] Add this EXACT string to Cognito Allowed callback URLs:", JSON.stringify(redirectUri));
    console.warn("[Cognito] Full login URL:", loginUrl);
    window.location.href = loginUrl;
  };

  useEffect(() => {
    setSession();
  }, []);

  return (
    <div style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      width: "100%",
      height: "100%",
      position: "absolute",
    }}
    >
      {
        parsedQueryParams[PARAMS.CODE]
          ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                position: "absolute",
                backgroundColor: COLORS.BLACK_MAIN,
                color: COLORS.WHITE,
              }}
            >
              { isAuthorized ? (
                <div>
                  <Spin size="large" />
                  <div>Loading...</div>
                </div>
              ) : (
                <div>
                  Sorry, your user is NOT AUTHORIZED for this site.
                  <br />
                  <br />
                  {(authorizedSites.length === 0) ? "" : "Sites that your user is authorized for:"}
                  <ul>
                    {
                      authorizedSites.map((site) => {
                        return (
                          <li>
                            <a href={getRedirectURL(site.title)}>
                              {site.title}
                              &nbsp;Operator
                            </a>
                          </li>
                        );
                      })
                    }
                  </ul>
                </div>
              )}
            </div>
          ) : (<div />)
      }
    </div>
  );
});

export default LoginPage;
